import { z } from 'zod';

/**
 * Модерация: что нельзя генерировать и как отвечаем юзеру.
 * Правила и тексты — здесь; проверки — стоп-лист (быстро, без сети) + LLM / vision-модель (packages/moderation).
 */

export const MODERATION_CATEGORIES = ['nsfw', 'minor', 'violence', 'celebrity', 'hate', 'other'] as const;
export type ModerationCategory = (typeof MODERATION_CATEGORIES)[number];

export const MODERATION_CATEGORY_LABELS: Record<ModerationCategory, string> = {
  nsfw: '18+ / обнажёнка',
  minor: 'несовершеннолетние',
  violence: 'насилие, кровь',
  celebrity: 'знаменитость',
  hate: 'ненависть, экстремизм',
  other: 'другое',
};

/** ok — пропускаем; flag — пропускаем и ставим флаг в очередь; block — не делаем. */
export type ModerationAction = 'ok' | 'flag' | 'block';

export interface ModerationVerdict {
  action: ModerationAction;
  categories: ModerationCategory[];
  /** Короткая причина (для очереди модерации). */
  reason: string | null;
}

export const OK_VERDICT: ModerationVerdict = { action: 'ok', categories: [], reason: null };

export const MODERATION_RULES = {
  /** Лицо на входе младше — «ребёнок»: только в трендах с флагом «можно детей». Возраст — оценка модели (±5 лет). */
  minorAge: 18,
  /** Двойник — это сам юзер (18+): фото, где лицо явно детское, не принимаем. */
  twinMinorAge: 16,
  /** Нарушений за 30 дней — после стольких подтверждённых флагов админка подсветит юзера. */
  strikesWarn: 3,
} as const;

/** Что видит юзер. */
export const MODERATION_TEXTS = {
  prompt_blocked: 'Такое сделать не можем: без 18+, насилия и реальных знаменитостей. Попробуй переформулировать.',
  nsfw_input: 'На фото откровенный контент — такое не принимаем. Выбери другое фото.',
  minor_not_allowed: 'Похоже, на фото ребёнок. С детьми — только тренды из раздела «Дети, питомцы».',
  twin_minor: 'Похоже, на фото ребёнок — двойник делается только из твоих фото',
  output_blocked: 'Результат не прошёл модерацию — показать не можем. Деньги вернули.',
} as const;

// ---------- Стоп-лист ----------

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ');

/** Слова-основы: совпадение с началом слова. */
const STEMS: Record<Exclude<ModerationCategory, 'other' | 'minor'>, string[]> = {
  nsfw: [
    'порн', 'голая', 'голый', 'голые', 'голой', 'голым', 'обнажен', 'нагая', 'нагой', 'нагишом', 'секс', 'эроти', 'топлес', 'без одежды', 'без трусов',
    'без лифчика', 'соски', 'сиськ', 'пизд', 'хуй', 'минет', 'оргазм', 'стриптиз', 'nude', 'naked', 'nsfw', 'porn', 'sex', 'topless', 'nipple',
    'boobs', 'erotic', 'hentai', 'хентай', 'onlyfans', 'xxx', 'фетиш', 'bdsm', 'бдсм',
  ],
  violence: ['расчлен', 'обезглав', 'кишки наружу', 'gore', 'decapitat', 'dismember', 'расстрел', 'повешен', 'самоубий', 'suicide', 'кровь из'],
  celebrity: [
    'путин', 'putin', 'трамп', 'trump', 'зеленск', 'zelensk', 'байден', 'biden', 'илон маск', 'elon musk', 'тейлор свифт', 'taylor swift', 'папа римск',
    'дуров', 'durov', 'моргенштерн', 'бузова', 'киркоров',
  ],
  hate: ['свастик', 'swastika', 'нацист', 'nazi', 'игил', 'isis', 'хайль', 'heil hitler', 'гитлер', 'hitler'],
};
const CHILD = ['ребен', 'ребён', 'детск', 'девочк', 'мальчик', 'школьниц', 'школьник', 'малыш', 'подрост', 'child', 'kid', 'teen', 'loli', 'лоли', 'underage', 'несовершеннолет'];

function hit(text: string, stems: string[]): string | null {
  for (const s of stems) {
    const re = new RegExp(`(^|\\s)${s.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'u');
    if (re.test(text)) return s.trim();
  }
  return null;
}

/**
 * Быстрая проверка текста (промпт, поля тренда) без сети.
 * Сексуальное + детское — block «minor»; 18+ / насилие / ненависть / знаменитость — block.
 */
export function checkTextLocal(raw: string): ModerationVerdict {
  const text = ` ${norm(raw)} `;
  const nsfw = hit(text, STEMS.nsfw);
  if (nsfw && hit(text, CHILD)) return { action: 'block', categories: ['minor', 'nsfw'], reason: `стоп-лист: ${nsfw} + ребёнок` };
  for (const cat of ['nsfw', 'violence', 'hate', 'celebrity'] as const) {
    const w = hit(text, STEMS[cat]);
    if (w) return { action: 'block', categories: [cat], reason: `стоп-лист: ${w}` };
  }
  return OK_VERDICT;
}

/** Ответ LLM/vision → вердикт. Непонятный ответ — ok (сбой проверки не наказывает юзера, но флаг ставит вызывающий). */
export function parseModeration(text: string): ModerationVerdict | null {
  const m = /\{[\s\S]*\}/.exec(text);
  try {
    const v = JSON.parse(m ? m[0] : text) as { action?: unknown; categories?: unknown; reason?: unknown };
    const action = v.action === 'block' || v.action === 'flag' || v.action === 'ok' ? v.action : null;
    if (!action) return null;
    const categories = (Array.isArray(v.categories) ? v.categories : []).filter((c): c is ModerationCategory =>
      (MODERATION_CATEGORIES as readonly string[]).includes(String(c)),
    );
    return { action, categories, reason: typeof v.reason === 'string' && v.reason ? v.reason.slice(0, 200) : null };
  } catch {
    return null;
  }
}

/** Из нескольких вердиктов — самый строгий. */
export function strictest(...vs: (ModerationVerdict | null | undefined)[]): ModerationVerdict {
  const order: ModerationAction[] = ['ok', 'flag', 'block'];
  let out = OK_VERDICT;
  for (const v of vs) {
    if (!v) continue;
    if (order.indexOf(v.action) > order.indexOf(out.action)) out = v;
    else if (v.action === out.action && v.action !== 'ok') out = { ...out, categories: [...new Set([...out.categories, ...v.categories])] };
  }
  return out;
}

// ---------- Флаги и жалобы ----------

export const FLAG_SOURCES = ['prompt', 'input', 'output', 'report'] as const;
export type FlagSource = (typeof FLAG_SOURCES)[number];
export const FLAG_STATUSES = ['open', 'confirmed', 'dismissed'] as const;

export const REPORT_REASONS = [
  { id: 'my_face', label: 'Моё лицо использовали без согласия' },
  { id: 'nsfw', label: '18+ или непристойное' },
  { id: 'minor', label: 'На фото ребёнок в неподобающем виде' },
  { id: 'other', label: 'Другое' },
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number]['id'];

export const reportRequestSchema = z.object({
  reason: z.enum(REPORT_REASONS.map((r) => r.id) as [ReportReason, ...ReportReason[]]),
  generationId: z.string().uuid().optional(),
  text: z.string().trim().max(1000).default(''),
});
export type ReportRequest = z.input<typeof reportRequestSchema>;
